import {ConstraintType, DataTypeConstraintDto} from "../../../../../common/apis/datatype/DataTypeConstraintDto";
import {AttributeDefinitionDto} from "../../../../../common/apis/stereotype/AttributeDefinitionDto";
import {UUIDUtils} from "../../../../../common/UUIDUtils";
import {CheckboxField} from "./model/CheckboxField";
import {DatePickerField} from "./model/DatePickerField";
import {DateTimePickerField} from "./model/DateTimePickerField";
import {NumberField} from "./model/NumberField";
import {SelectField} from "./model/SelectField";
import {TextField} from "./model/TextField";
import {TimePickerField} from "./model/TimePickerField";
import {UiField} from "./model/UiField";

type UiFieldFactoryProvider = (attributeDefinition: AttributeDefinitionDto) => UiField;

export class UiFieldFactory {

    private readonly fieldFactoryProviders: { [key: string]: UiFieldFactoryProvider };
    private readonly fallbackFieldFactoryProvider: UiFieldFactoryProvider;

    constructor() {
        this.fieldFactoryProviders = {
            "STRING": this.buildStringField.bind(this),
            "BOOLEAN": this.buildCheckboxField.bind(this),
            "DATE": this.buildDatePickerField.bind(this),
            "TIME": this.buildTimePickerField.bind(this),
            "DATETIME": this.buildDateTimePickerField.bind(this),
            "INTEGER": this.buildNumberField.bind(this),
            "FLOAT": this.buildNumberField.bind(this),
            "CURRENCY": this.buildNumberField.bind(this),
        };

        this.fallbackFieldFactoryProvider = this.buildTextField;
    }

    buildUiField(attributeDefinition: AttributeDefinitionDto): UiField {
        if (this.fieldFactoryProviders[attributeDefinition.dataType.coreDataType]) {
            return this.fieldFactoryProviders[attributeDefinition.dataType.coreDataType](attributeDefinition);
        } else {
            return this.fallbackFieldFactoryProvider(attributeDefinition);
        }
    }

    private buildStringField(attributeDefinition: AttributeDefinitionDto): UiField {
        if (this.containsEnumConstraint(attributeDefinition.dataType.constraints)) {
            return this.buildSelectField(attributeDefinition);
        } else {
            return this.buildTextField(attributeDefinition);
        }
    }

    private containsEnumConstraint(constraints: DataTypeConstraintDto[] | undefined): boolean {
        if (!constraints) {
            return false;
        }
        return constraints.some(constraint => constraint.constraintData.type === ConstraintType.ENUMERATION);
    }

    private buildTextField(attributeDefinition: AttributeDefinitionDto): UiField {
        return {
            id: UUIDUtils.randomUUID(),
            componentType: "TextField",
            attributeDefinitionId: attributeDefinition.id,
        } as TextField;
    }

    private buildSelectField(attributeDefinition: AttributeDefinitionDto): UiField {
        return {
            id: UUIDUtils.randomUUID(),
            componentType: "SelectField",
            attributeDefinitionId: attributeDefinition.id,
        } as SelectField;
    }

    private buildCheckboxField(attributeDefinition: AttributeDefinitionDto): UiField {
        return {
            id: UUIDUtils.randomUUID(),
            componentType: "CheckboxField",
            attributeDefinitionId: attributeDefinition.id,
        } as CheckboxField;
    }

    private buildDatePickerField(attributeDefinition: AttributeDefinitionDto): UiField {
        return {
            id: UUIDUtils.randomUUID(),
            componentType: "DatePickerField",
            attributeDefinitionId: attributeDefinition.id,
        } as DatePickerField;
    }

    private buildTimePickerField(attributeDefinition: AttributeDefinitionDto): UiField {
        return {
            id: UUIDUtils.randomUUID(),
            componentType: "TimePickerField",
            attributeDefinitionId: attributeDefinition.id,
        } as TimePickerField;
    }

    private buildDateTimePickerField(attributeDefinition: AttributeDefinitionDto): UiField {
        return {
            id: UUIDUtils.randomUUID(),
            componentType: "DateTimePickerField",
            attributeDefinitionId: attributeDefinition.id,
        } as DateTimePickerField;
    }

    private buildNumberField(attributeDefinition: AttributeDefinitionDto): UiField {
        return {
            id: UUIDUtils.randomUUID(),
            componentType: "NumberField",
            attributeDefinitionId: attributeDefinition.id,
        } as NumberField;
    }
}